// @flow
import React, { useState, type Node } from 'react';
import Sticky from 'react-stickynode';
import classnames from 'classnames';
import { UnmountClosed } from 'react-collapse';

import { DropZone } from 'Components/frameworks/drop-zone/DropZone.jsx';
import { ProductCondensed } from 'Components/widgets/product-condensed/ProductCondensed.jsx';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer.jsx';
import { SearchCompareButton } from 'Components/widgets/facet-search-results/search-compare-button/SearchCompareButton.jsx';

type Props = {
  buttonLabel: string,
  cancelLabel: string,
  children?: Node,
  className?: string,
  comparisonClick: () => void,
  comparisonToggle: () => void,
  comparisonUrl: string,
  dropzoneLabel?: string,
  isComparisonActive?: boolean,
  isComparisonSubmitable?: boolean,
  mandatoryMessage?: string,
  resultsCount?: number,
  selectedItems?: Array<*>,
  selectedItemsMax: number,
  toggleProductSelectedForComparison: ?(string) => *,
};

export function SearchCompareLarge(props: Props) {
  const {
    buttonLabel,
    cancelLabel,
    children,
    className,
    comparisonClick,
    comparisonToggle,
    comparisonUrl,
    dropzoneLabel,
    isComparisonActive,
    isComparisonSubmitable,
    mandatoryMessage,
    resultsCount,
    selectedItems = [],
    selectedItemsMax = 3,
    toggleProductSelectedForComparison,
  } = props;

  const [isSticky, setSticky] = useState(false);

  const onStateChange = (status: *) => {
    setSticky(status.status !== Sticky.STATUS_ORIGINAL);
  };

  return (
    <Sticky bottomBoundary="#SearchCompareBeacon" innerZ={2} onStateChange={onStateChange}>
      <section
        className={classnames(className, 'bg-white py-2', {
          'border-b-1 border-gray-light shadow-bottom': isSticky,
        })}
      >
        <SiteContainer>
          <div className="flex items-center justify-between">
            <div className="flex-1">{children}</div>
            {isComparisonActive && (
              <button
                className="reset-button font-bold text-base mx-5"
                onClick={comparisonToggle}
                type="button"
              >
                {cancelLabel}
              </button>
            )}
            {resultsCount &&
              resultsCount > 0 && (
                <SearchCompareButton
                  comparisonToggle={comparisonToggle}
                  comparisonClick={comparisonClick}
                  comparisonUrl={comparisonUrl}
                  isComparisonActive={isComparisonActive}
                  isComparisonSubmitable={isComparisonSubmitable}
                  label={buttonLabel}
                  mandatoryMessage={mandatoryMessage}
                  selectedItemsCount={selectedItems.length}
                  selectedItemsMax={selectedItemsMax}
                  style={{ minWidth: 280, minHeight: 40 }}
                />
              )}
          </div>
        </SiteContainer>
        <UnmountClosed isOpened={isComparisonActive}>
          <SiteContainer>
            <div className="flex -mx-2 pt-3">
              {Array(selectedItemsMax)
                .fill()
                .map((_, idx) => {
                  const product = selectedItems[idx];
                  const productId = product && product.id;

                  return (
                    <div className="p-2 w-1/3" key={productId || idx}>
                      <DropZone
                        label={dropzoneLabel}
                        number={idx + 1}
                        onClear={
                          productId && toggleProductSelectedForComparison
                            ? () => toggleProductSelectedForComparison(productId)
                            : undefined
                        }
                      >
                        {product && <ProductCondensed {...product} />}
                      </DropZone>
                    </div>
                  );
                })}
            </div>
          </SiteContainer>
        </UnmountClosed>
      </section>
    </Sticky>
  );
}
